import React from 'react';
import { RefreshCw } from 'lucide-react';

const CallsConnectionModal = ({
  isOpen,
  onClose,
  callsConnectionError,
  onShortcutsConnect,
  onTwilioConnect,
  twilioAccountSid,
  setTwilioAccountSid,
  twilioAuthToken,
  setTwilioAuthToken,
  twilioPhoneNumber,
  setTwilioPhoneNumber,
  connectingCalls
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white mb-1">Connect Your Calls</h3>
          <p className="text-sm text-gray-400">Choose how you want to sync your call history</p>
        </div>
        
        <div className="p-6 space-y-4">
          {callsConnectionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {callsConnectionError}
            </div>
          )}
          
          {/* iPhone Shortcuts Option */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-blue-500/50 transition-colors">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <svg className="w-8 h-8 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-white font-semibold mb-1">iPhone via Shortcuts App</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Sync your call history using Apple Shortcuts automation. Your data stays private.
                </p>
                <div className="space-y-2 text-xs text-gray-500 mb-4">
                  <p>1. Download the Call Log Shortcut</p>
                  <p>2. Run it to sync your recent calls</p>
                  <p>3. Automate it for continuous sync</p>
                </div>
                <button
                  onClick={onShortcutsConnect}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="calls-shortcuts-connect-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Connect iPhone Calls
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-700"></div>
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-gray-700"></div>
          </div>
          
          {/* Twilio Voice Option */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-red-500/50 transition-colors">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-500/20 rounded-xl">
                <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-white font-semibold mb-1">Twilio Voice</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Make and receive calls via Twilio. Requires a voice-enabled Twilio number.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Account SID</label>
                    <input
                      type="text"
                      value={twilioAccountSid}
                      onChange={(e) => setTwilioAccountSid(e.target.value)}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      data-testid="calls-twilio-account-sid"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Auth Token</label>
                    <input
                      type="password"
                      value={twilioAuthToken}
                      onChange={(e) => setTwilioAuthToken(e.target.value)}
                      placeholder="Your auth token"
                      className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      data-testid="calls-twilio-auth-token"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Phone Number</label>
                    <input
                      type="text"
                      value={twilioPhoneNumber}
                      onChange={(e) => setTwilioPhoneNumber(e.target.value)}
                      placeholder="+1234567890"
                      className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      data-testid="calls-twilio-phone"
                    />
                  </div>
                  <button
                    onClick={onTwilioConnect}
                    disabled={connectingCalls}
                    className="w-full bg-[#F22F46] hover:bg-[#d62a3e] disabled:bg-gray-600 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                    data-testid="calls-twilio-connect-btn"
                  >
                    {connectingCalls ? (
                      <>
                        <RefreshCw size={18} className="animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                        </svg>
                        Connect Twilio Voice
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallsConnectionModal;
